import React, { useEffect, useRef } from "react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { MapPin, Globe2, ArrowUpRight } from "lucide-react";
import { useRFQ } from "../context/RFQContext";

export default function GlobalPresence() {
  const { openRFQ } = useRFQ();
  const { content } = useContent();
  const regions = content.regions || [];
  const mapRef = useRef(null);
  // On phones the map is wider than the screen: start centred on Europe / Africa / Middle East.
  useEffect(() => {
    const el = mapRef.current;
    if (el && window.innerWidth < 768) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, []);
  const countriesStat = (content.stats || [])[3];
  return (
    <>
      <PageHero
        eyebrow="Global Presence"
        crumb="Global Presence"
        title="Serving healthcare worldwide"
        subtitle="Proudly serving pharmaceutical companies across the Middle East, Africa, North Africa, Latin America, Asia, CIS Countries, Europe, the Balkans and the US."
      />

      {/* World map with served regions (from the Novocreste company profile) */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-2 text-[#2ED9B8] text-xs uppercase tracking-[0.2em]">
              <Globe2 className="h-4 w-4" /> {countriesStat ? `${countriesStat.value} ${countriesStat.label}` : "Countries served"}
            </div>
            <h2 className="mt-3 font-display text-[28px] lg:text-[40px] text-foreground leading-[1.1] max-w-2xl">
              A truly international sourcing footprint
            </h2>
          </Reveal>
          <Reveal delay={80} className="mt-8 rounded-[28px] overflow-hidden border border-white/10 bg-[#0a1a2b]">
            {/* Crop the empty Antarctica strip at the bottom of the source image */}
            <div ref={mapRef} className="overflow-x-auto"><div className="aspect-[2000/850] overflow-hidden min-w-[1100px] md:min-w-[760px]">
              <img
                src="/images/global-presence-map.png"
                alt={`World map showing regions we serve: ${regions.join(", ")}`}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div></div>
          </Reveal>
          <p className="mt-3 text-xs text-muted-foreground text-center md:hidden">Swipe sideways to explore the map</p>
        </div>
      </section>

      {/* Regions grid */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display text-[30px] lg:text-[40px] text-foreground">Markets we serve</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4">
            {regions.map((r, i) => (
              <Reveal key={r} delay={(i % 3) * 70}>
                <div className="group flex items-center gap-4 bg-card rounded-2xl border border-white/10 px-5 py-4 hover:border-[#2ED9B8]/25 transition-colors duration-300">
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-[#0f2236] flex items-center justify-center group-hover:bg-[#00ab96] transition-colors">
                    <MapPin className="h-5 w-5 text-[#2ED9B8] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display text-[18px] lg:text-[20px] text-foreground">{r}</h3>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120} className="mt-12">
            <button onClick={openRFQ} className="inline-flex items-center gap-2 bg-[#00ab96] hover:bg-[#0b8f7e] text-white rounded-full px-7 h-12 font-medium transition-colors">
              Source in your market <ArrowUpRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

    </>
  );
}
